import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Search, Download, ChevronDown, ChevronUp, ChevronRight,
  MapPin, Phone, Mail, FileText, Users, Globe, Building2,
  Loader2, ExternalLink, User, Calendar, Hash,
} from 'lucide-react';
import { toast } from 'sonner';
import operatorService from '../services/operatorService';
import * as XLSX from 'xlsx';

// ── helpers ──────────────────────────────────────────────────────────────────

function statusBadge(status) {
  const s = (status || '').toLowerCase();
  const cls =
    s === 'active'   ? 'bg-emerald-100 text-emerald-800 border-emerald-200' :
    s === 'pending'  ? 'bg-yellow-100 text-yellow-800 border-yellow-200' :
    ['inactive', 'expired', 'revoked', 'cancelled'].includes(s)
                     ? 'bg-red-100 text-red-800 border-red-200' :
    s === 'suspended'? 'bg-orange-100 text-orange-800 border-orange-200' :
                       'bg-gray-100 text-gray-600 border-gray-200';
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border capitalize ${cls}`}>
      {status || 'Unknown'}
    </span>
  );
}

function fmtDate(d) {
  if (!d) return null;
  return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function Field({ label, value, className = '' }) {
  if (!value) return null;
  return (
    <div className={className}>
      <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide">{label}</p>
      <p className="text-sm font-medium text-foreground mt-0.5">{value}</p>
    </div>
  );
}

// ── License row (expandable) ─────────────────────────────────────────────────

function LicenseRow({ license, idx }) {
  const [open, setOpen] = useState(false);
  const contacts = license.contacts ?? [];
  const hasContacts = contacts.length > 0;

  return (
    <div className="border border-border rounded-xl overflow-hidden">
      {/* License header — always visible */}
      <button
        onClick={() => setOpen(v => !v)}
        className="w-full flex items-center gap-3 px-4 py-3 bg-muted/30 hover:bg-muted/60 transition-colors text-left"
      >
        <span className={`flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold shrink-0
          ${license.licenseStatus?.toLowerCase() === 'active'
            ? 'bg-emerald-100 text-emerald-700'
            : 'bg-gray-100 text-gray-600'}`}>
          {idx + 1}
        </span>

        <div className="flex-1 flex flex-wrap items-center gap-2 min-w-0">
          <span className="font-mono text-sm font-semibold truncate">
            {license.licenseNumber || `License ${idx + 1}`}
          </span>
          {license.licenseStateAbbr && (
            <Badge variant="secondary" className="text-xs shrink-0">{license.licenseStateAbbr}</Badge>
          )}
          {license.licenseType && (
            <span className="text-xs text-muted-foreground truncate">{license.licenseType}</span>
          )}
          {license.vertical && (
            <span className="text-xs text-muted-foreground hidden sm:inline">· {license.vertical}</span>
          )}
        </div>

        <div className="flex items-center gap-3 shrink-0">
          {statusBadge(license.licenseStatus)}
          {hasContacts && (
            <span className="flex items-center gap-1 text-xs text-muted-foreground">
              <Users className="w-3 h-3" />{contacts.length}
            </span>
          )}
          {open ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
        </div>
      </button>

      {/* Expanded license detail + contacts */}
      {open && (
        <div className="px-4 py-4 bg-white border-t border-border space-y-4">
          {/* License fields — two-column grid */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-6 gap-y-3">
            <Field label="License Type"    value={license.licenseType} />
            <Field label="Designation"     value={license.licenseDesignation} />
            <Field label="Vertical"        value={license.vertical} />
            <Field label="Category"        value={license.category} />
            <Field label="Activity"        value={license.activityOrFacility} />
            <Field label="Term"            value={license.licenseTerm} />
            <Field label="Status"          value={license.operationalStatus} />
            <Field label="Issue Date"      value={fmtDate(license.issueDate)} />
            <Field label="Expiration"      value={fmtDate(license.expirationDate)} />
            <Field label="Renewal Due"     value={fmtDate(license.renewalDueDate)} />
            <Field label="State"           value={license.licenseStateAbbr} />
          </div>

          {/* License-level business info (if different from corporate) */}
          {(license.businessName || license.email || license.phone || license.streetAddress) && (
            <div className="pt-3 border-t border-dashed border-border">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">License Location</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-2">
                <Field label="Business Name" value={license.businessName} />
                <Field label="DBA"           value={license.dbaName} />
                <Field label="Phone"         value={license.phone} />
                <Field label="Email"         value={license.email} />
                {license.streetAddress && (
                  <div className="col-span-2">
                    <Field label="Address"
                      value={[license.streetAddress, license.city, license.zipCode, license.addressStateAbbr].filter(Boolean).join(', ')}
                    />
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Contacts for this license */}
          {hasContacts && (
            <div className="pt-3 border-t border-dashed border-border">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2 flex items-center gap-1">
                <Users className="w-3 h-3" /> Contacts ({contacts.length})
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {contacts.map((c, ci) => (
                  <div key={c.operatorContactId ?? ci}
                    className="flex items-start gap-3 p-3 rounded-lg bg-muted/30 border border-border/60">
                    <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                      <User className="w-4 h-4 text-primary" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold">
                        {[c.contactFirstName, c.contactLastName].filter(Boolean).join(' ') || `Contact ${ci + 1}`}
                      </p>
                      {c.contactDesignation && (
                        <p className="text-xs text-muted-foreground">{c.contactDesignation}</p>
                      )}
                      {c.contactPhone && (
                        <p className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
                          <Phone className="w-3 h-3 shrink-0" />{c.contactPhone}
                        </p>
                      )}
                      {c.contactEmail && (
                        <p className="text-xs text-muted-foreground flex items-center gap-1">
                          <Mail className="w-3 h-3 shrink-0" />{c.contactEmail}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Operator detail panel (right-side slide-in) ───────────────────────────────

function OperatorPanel({ operator: op, onClose }) {
  const [tab, setTab] = useState('licenses');
  if (!op) return null;

  const licenses   = op.licenses   ?? [];
  const allStates  = [...new Set(licenses.map(l => l.licenseStateAbbr).filter(Boolean))];
  const totalContacts = licenses.reduce((sum, l) => sum + (l.contacts?.length ?? 0), 0);
  const activeCount   = licenses.filter(l => l.licenseStatus?.toLowerCase() === 'active').length;

  const tabs = [
    { key: 'licenses', label: `Licenses (${licenses.length})`, icon: FileText },
    { key: 'corporate', label: 'Corporate Info', icon: Building2 },
  ];

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" onClick={onClose} />

      {/* Panel */}
      <div className="relative w-full max-w-2xl bg-white shadow-2xl flex flex-col h-full overflow-hidden">

        {/* Panel header */}
        <div className="px-6 py-5 bg-gradient-to-r from-emerald-600 to-green-500 text-white shrink-0">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <h2 className="text-lg font-bold truncate">{op.corporateName || '—'}</h2>
              {(() => {
                const firstLic = (op.licenses ?? [])[0];
                const biz = firstLic?.businessName;
                return biz && biz !== op.corporateName
                  ? <p className="text-emerald-100 text-sm truncate">{biz}{firstLic?.dbaName && ` · DBA: ${firstLic.dbaName}`}</p>
                  : null;
              })()}
            </div>
            <button onClick={onClose}
              className="p-1.5 rounded-lg bg-white/20 hover:bg-white/30 transition-colors shrink-0">
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>

          {/* Quick stats */}
          <div className="flex items-center gap-4 mt-4 text-sm">
            <div className="flex items-center gap-1.5 bg-white/15 rounded-full px-3 py-1">
              <FileText className="w-3.5 h-3.5" />
              <span>{licenses.length} license{licenses.length !== 1 ? 's' : ''}</span>
              {activeCount > 0 && <span className="text-emerald-200">({activeCount} active)</span>}
            </div>
            {allStates.length > 0 && (
              <div className="flex items-center gap-1.5 bg-white/15 rounded-full px-3 py-1">
                <MapPin className="w-3.5 h-3.5" />
                <span>{allStates.join(', ')}</span>
              </div>
            )}
            {totalContacts > 0 && (
              <div className="flex items-center gap-1.5 bg-white/15 rounded-full px-3 py-1">
                <Users className="w-3.5 h-3.5" />
                <span>{totalContacts} contact{totalContacts !== 1 ? 's' : ''}</span>
              </div>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-border shrink-0">
          {tabs.map(t => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`flex items-center gap-2 px-5 py-3 text-sm font-medium border-b-2 transition-colors
                ${tab === t.key
                  ? 'border-emerald-600 text-emerald-700'
                  : 'border-transparent text-muted-foreground hover:text-foreground'}`}
            >
              <t.icon className="w-4 h-4" />
              {t.label}
            </button>
          ))}
        </div>

        {/* Tab content — scrollable */}
        <div className="flex-1 overflow-y-auto p-5 space-y-3">

          {/* ── LICENSES TAB ── */}
          {tab === 'licenses' && (
            <>
              {licenses.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-40 text-muted-foreground">
                  <FileText className="w-10 h-10 mb-2 opacity-30" />
                  <p className="text-sm">No licenses on record</p>
                </div>
              ) : (
                licenses.map((l, i) => <LicenseRow key={l.operatorLicenseId ?? i} license={l} idx={i} />)
              )}
            </>
          )}

          {/* ── CORPORATE TAB ── */}
          {tab === 'corporate' && (
            <div className="space-y-5">
              {/* Identity */}
              <section className="rounded-xl border border-border p-4 space-y-3">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Identity</p>
                <div className="grid grid-cols-2 gap-x-6 gap-y-3">
                  <Field label="Corporate Name"  value={op.corporateName} />
                  <Field label="# Licenses"      value={op.licenseCount ?? licenses.length} />
                  <Field label="# States"        value={op.licenseStateCount ?? allStates.length} />
                  <Field label="# License Types" value={op.licensetypeCount} />
                  {op.notes && <div className="col-span-2"><Field label="Notes" value={op.notes} /></div>}
                </div>
              </section>

              {/* Corporate contact person */}
              {(op.corpFirstName || op.corpLastName || op.corpDesignation) && (
                <section className="rounded-xl border border-border p-4 space-y-3">
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Primary Contact</p>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center shrink-0">
                      <User className="w-5 h-5 text-emerald-700" />
                    </div>
                    <div>
                      <p className="font-semibold">
                        {[op.corpFirstName, op.corpLastName].filter(Boolean).join(' ') || '—'}
                      </p>
                      {op.corpDesignation && <p className="text-sm text-muted-foreground">{op.corpDesignation}</p>}
                    </div>
                  </div>
                </section>
              )}

              {/* Contact details */}
              <section className="rounded-xl border border-border p-4 space-y-3">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Contact Details</p>
                <div className="grid grid-cols-2 gap-x-6 gap-y-3">
                  <Field label="Phone"   value={op.corpPhone} />
                  <Field label="Email"   value={op.corpEmail} />
                  {op.corpWebsite && (
                    <div className="col-span-2">
                      <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide">Website</p>
                      <a href={op.corpWebsite} target="_blank" rel="noopener noreferrer"
                        className="text-sm text-emerald-600 hover:underline flex items-center gap-1 mt-0.5">
                        <Globe className="w-3 h-3" />{op.corpWebsite}
                      </a>
                    </div>
                  )}
                </div>
              </section>

              {/* Social */}
              {(op.corpFB || op.corpInsta || op.corpTwitter || op.corpLinkedIn) && (
                <section className="rounded-xl border border-border p-4 space-y-3">
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Social</p>
                  <div className="grid grid-cols-2 gap-2">
                    {[['Facebook', op.corpFB], ['Instagram', op.corpInsta],
                      ['Twitter', op.corpTwitter], ['LinkedIn', op.corpLinkedIn]]
                      .filter(([, url]) => url)
                      .map(([label, url]) => (
                        <a key={label} href={url} target="_blank" rel="noopener noreferrer"
                          className="flex items-center gap-1.5 text-sm text-emerald-600 hover:underline">
                          <ExternalLink className="w-3 h-3" />{label}
                        </a>
                      ))}
                  </div>
                </section>
              )}

            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Sort icon (defined outside component to keep stable reference) ────────────

function SortIcon({ sortKey, sortConfig }) {
  if (sortConfig.key !== sortKey) return <ChevronDown className="w-3 h-3 inline ml-0.5 opacity-30" />;
  return sortConfig.direction === 'asc'
    ? <ChevronUp className="w-3 h-3 inline ml-0.5 text-emerald-600" />
    : <ChevronDown className="w-3 h-3 inline ml-0.5 text-emerald-600" />;
}

// ── Main page ────────────────────────────────────────────────────────────────

export default function OperatorsPage() {
  const [operators, setOperators]   = useState([]);
  const [filtered, setFiltered]     = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'corporateName', direction: 'asc' });
  const [isLoading, setIsLoading]   = useState(true);
  const [selected, setSelected]     = useState(null);

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      setIsLoading(true);
      const data = await operatorService.getOperators();
      const list = Array.isArray(data) ? data : [];
      setOperators(list);
      setFiltered(list);
    } catch (err) {
      toast.error(typeof err === 'string' ? err : 'Failed to fetch operators');
      setOperators([]); setFiltered([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Search + sort
  useEffect(() => {
    let list = [...operators];
    if (searchTerm) {
      const t = searchTerm.toLowerCase();
      list = list.filter(op =>
        op.corporateName?.toLowerCase().includes(t) ||
        op.licenses?.some(l =>
          l.businessName?.toLowerCase().includes(t)  ||
          l.dbaName?.toLowerCase().includes(t)       ||
          l.licenseNumber?.toLowerCase().includes(t) ||
          l.licenseStateAbbr?.toLowerCase().includes(t) ||
          l.licenseType?.toLowerCase().includes(t)   ||
          l.city?.toLowerCase().includes(t)
        )
      );
    }
    list.sort((a, b) => {
      let av = '', bv = '';
      if (sortConfig.key === 'licenseNumber') {
        av = a.licenses?.[0]?.licenseNumber ?? '';
        bv = b.licenses?.[0]?.licenseNumber ?? '';
      } else if (sortConfig.key === 'state') {
        av = a.licenses?.[0]?.licenseStateAbbr ?? '';
        bv = b.licenses?.[0]?.licenseStateAbbr ?? '';
      } else {
        av = a[sortConfig.key] ?? '';
        bv = b[sortConfig.key] ?? '';
      }
      return sortConfig.direction === 'asc'
        ? String(av).localeCompare(String(bv))
        : String(bv).localeCompare(String(av));
    });
    setFiltered(list);
  }, [searchTerm, sortConfig, operators]);

  const handleSort = key =>
    setSortConfig(prev => ({ key, direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc' }));

  const handleExport = () => {
    try {
      const rows = filtered.flatMap(op =>
        (op.licenses?.length ? op.licenses : [{}]).flatMap(l => {
          const contacts = l.contacts?.length ? l.contacts : [{}];
          return contacts.map(c => ({
            'Corporate Name':    op.corporateName,
            'Corp Phone':        op.corpPhone,
            'Corp Email':        op.corpEmail,
            'Corp Person':       [op.corpFirstName, op.corpLastName].filter(Boolean).join(' '),
            'Business Name':     l.businessName,
            'DBA':               l.dbaName,
            'License #':         l.licenseNumber,
            'License State':     l.licenseStateAbbr,
            'License Type':      l.licenseType,
            'License Status':    l.licenseStatus,
            'Designation':       l.licenseDesignation,
            'Vertical':          l.vertical,
            'Category':          l.category,
            'Issue Date':        fmtDate(l.issueDate),
            'Expiry Date':       fmtDate(l.expirationDate),
            'Renewal Due':       fmtDate(l.renewalDueDate),
            'City':              l.city,
            'County':            l.county,
            'Zip':               l.zipCode,
            'Address State':     l.addressStateAbbr,
            'Contact Name':      [c.contactFirstName, c.contactLastName].filter(Boolean).join(' '),
            'Contact Role':      c.contactDesignation,
            'Contact Phone':     c.contactPhone,
            'Contact Email':     c.contactEmail,
          }));
        })
      );
      const ws = XLSX.utils.json_to_sheet(rows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Operators');
      XLSX.writeFile(wb, `operators_${new Date().toISOString().split('T')[0]}.xlsx`);
      toast.success('Exported successfully');
    } catch { toast.error('Export failed'); }
  };

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <Loader2 className="w-12 h-12 animate-spin mx-auto mb-4 text-primary" />
            <p className="text-muted-foreground">Loading operators...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-5 animate-fade-in">
        <Card>
          <CardHeader>
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <CardTitle>My Operators</CardTitle>
                <CardDescription>
                  {filtered.length} of {operators.length} operator{operators.length !== 1 ? 's' : ''}
                  {' — click a row to view licenses & contacts'}
                </CardDescription>
              </div>
              <Button onClick={handleExport} variant="outline" size="sm">
                <Download className="w-4 h-4 mr-2" />Export Excel
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {/* Search */}
            <div className="relative mb-5">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search by corporate name, business name, license #, state…"
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>

            {/* Table */}
            <div className="overflow-x-auto rounded-lg border border-border">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="bg-muted/40">
                    {[
                      { key: 'corporateName', label: 'Corporate Name' },
                      { key: 'businessName',  label: 'Business / DBA' },
                      { key: null,            label: 'Licenses' },
                      { key: 'state',         label: 'States' },
                      { key: null,            label: 'Status' },
                      { key: null,            label: 'Corp Contact' },
                    ].map(({ key, label }) => (
                      <th key={label}
                        className={`text-left py-3 px-4 text-xs font-semibold text-muted-foreground uppercase tracking-wide whitespace-nowrap border-b border-border
                          ${key ? 'cursor-pointer hover:text-foreground' : ''}`}
                        onClick={() => key && handleSort(key)}
                      >
                        {label}{key && <SortIcon sortKey={key} sortConfig={sortConfig} />}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtered.length === 0 && (
                    <tr>
                      <td colSpan={6} className="text-center py-16 text-muted-foreground border-b border-border">
                        No operators found matching your criteria.
                      </td>
                    </tr>
                  )}
                  {filtered.map(op => {
                    const licenses  = op.licenses ?? [];
                    const states    = [...new Set(licenses.map(l => l.licenseStateAbbr).filter(Boolean))];
                    const statuses  = [...new Set(licenses.map(l => l.licenseStatus).filter(Boolean))];
                    const totalCtx  = licenses.reduce((s, l) => s + (l.contacts?.length ?? 0), 0);
                    const rowBase   = 'py-3 px-4 border-b border-border';

                    return (
                      <tr
                        key={op.operatorId}
                        onClick={() => setSelected(op)}
                        className={`hover:bg-emerald-50/40 transition-colors cursor-pointer
                          ${selected?.operatorId === op.operatorId ? 'bg-emerald-50/60' : ''}`}
                      >
                        {/* Corporate Name */}
                        <td className={rowBase}>
                          <div className="font-semibold text-foreground">{op.corporateName || '—'}</div>
                          {(op.corpFirstName || op.corpLastName) && (
                            <div className="text-xs text-muted-foreground mt-0.5">
                              {[op.corpDesignation, op.corpFirstName, op.corpLastName].filter(Boolean).join(' ')}
                            </div>
                          )}
                        </td>

                        {/* Business / DBA — from first license */}
                        <td className={rowBase}>
                          {(() => {
                            const fl = licenses[0];
                            return fl?.businessName
                              ? <>
                                  <div className="text-sm font-medium">{fl.businessName}</div>
                                  {fl.dbaName && <div className="text-xs text-muted-foreground">DBA: {fl.dbaName}</div>}
                                  {fl.city && <div className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5"><MapPin className="w-3 h-3" />{[fl.city, fl.addressStateAbbr].filter(Boolean).join(', ')}</div>}
                                </>
                              : <span className="text-muted-foreground">—</span>;
                          })()}
                        </td>

                        {/* Licenses */}
                        <td className={rowBase}>
                          <div className="flex items-center gap-2">
                            <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-emerald-100 text-emerald-700 text-xs font-bold">
                              {licenses.length}
                            </span>
                            {totalCtx > 0 && (
                              <span className="text-xs text-muted-foreground flex items-center gap-0.5">
                                <Users className="w-3 h-3" />{totalCtx}
                              </span>
                            )}
                          </div>
                        </td>

                        {/* States */}
                        <td className={rowBase}>
                          <div className="flex flex-wrap gap-1">
                            {states.length === 0 && <span className="text-muted-foreground">—</span>}
                            {states.slice(0, 3).map(s => (
                              <Badge key={s} variant="secondary" className="text-xs">{s}</Badge>
                            ))}
                            {states.length > 3 && (
                              <Badge variant="outline" className="text-xs">+{states.length - 3}</Badge>
                            )}
                          </div>
                        </td>

                        {/* Status */}
                        <td className={rowBase}>
                          <div className="flex flex-wrap gap-1">
                            {statuses.length === 0 && <span className="text-muted-foreground">—</span>}
                            {[...new Set(statuses)].slice(0, 2).map(s => (
                              <span key={s}>{statusBadge(s)}</span>
                            ))}
                          </div>
                        </td>

                        {/* Corp Contact */}
                        <td className={rowBase}>
                          {(op.corpPhone || op.corpEmail) ? (
                            <div className="space-y-0.5">
                              {op.corpPhone && (
                                <div className="text-xs text-muted-foreground flex items-center gap-1">
                                  <Phone className="w-3 h-3 shrink-0" />{op.corpPhone}
                                </div>
                              )}
                              {op.corpEmail && (
                                <div className="text-xs text-muted-foreground flex items-center gap-1">
                                  <Mail className="w-3 h-3 shrink-0" /><span className="truncate max-w-[140px]">{op.corpEmail}</span>
                                </div>
                              )}
                            </div>
                          ) : <span className="text-muted-foreground">—</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Right-side detail panel */}
      {selected && <OperatorPanel operator={selected} onClose={() => setSelected(null)} />}
    </DashboardLayout>
  );
}
